import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Link,
  Rating,
  Stack,
  Typography,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Talent, UserWorkStatus } from "../../types/users";
import { useNavigate } from "react-router-dom";
import TalentDrawer from "./TalentDrawer";
import { useGetTalentsQuery } from "../../services/talent.service";
import { useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import { FilterAlt, FilterAltOutlined } from "@mui/icons-material";
import ModalTalentFilter from "./ModalTalentFilter";
import { getDistanceBetweenPoints } from "../../utils/mapHelper";
import { filterPrivateTalent } from "../../utils/helper";
import { useCompany } from "../../hooks/useCompany";
import { CardBase, GlassDataGrid, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const TableToolbar = ({
  onSearch,
  onShowMap,
  onShowFav,
}: {
  onSearch: (query?: string) => void;
  onShowMap: () => void;
  onShowFav: () => void;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const filterLabel = useUILabel("common.filter");
  const showMapLabel = useUILabel("talent.showMap");
  const favoritesLabel = useUILabel("talent.favorites");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);
  const [showFilter, setShowFilter] = React.useState(false);

  const searchFilter = useAppSelector(
    (state: RootState) => state.ui.talentFilter
  );

  const filterOn = React.useMemo(() => {
    return (
      (Array.isArray(searchFilter.skills) && searchFilter.skills.length > 0) ||
      (Array.isArray(searchFilter.business_verticals) &&
        searchFilter.business_verticals.length > 0) ||
      (Array.isArray(searchFilter.certificates) &&
        searchFilter.certificates.length > 0) ||
      !!searchFilter.state ||
      !!searchFilter.city ||
      !!searchFilter.work_status
    );
  }, [searchFilter]);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery, onSearch]);

  return (
    <GridToolbarContainer
      sx={{
        padding: "16px 20px",
        marginBottom: 0,
        backgroundColor: "transparent",
        display: "flex",
        alignItems: "center",
        gap: 2,
        minHeight: "64px",
        width: "100%",
      }}
    >
      <Box flex={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <StyledTextField
          margin="dense"
          label={enterToSearchLabel}
          value={query || ""}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        {!!query && (
          <Button variant="text" sx={{ mt: 1 }} onClick={() => setQuery("")}>
            {resetLabel}
          </Button>
        )}
        <Button
          variant={filterOn ? "contained" : "outlined"}
          color="success"
          onClick={() => setShowFilter(true)}
          startIcon={filterOn ? <FilterAlt /> : <FilterAltOutlined />}
          sx={{
            ml: 1,
            mt: 0,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 140,
            color: filterOn ? "#fff" : "#12903C",
            borderColor: "#12903C",
            backgroundColor: filterOn ? "#12903C" : "transparent",
            boxShadow: filterOn
              ? isDark
                ? "0 4px 12px rgba(18, 144, 60, 0.4)"
                : "0 4px 12px rgba(18, 144, 60, 0.3)"
              : "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-2px)",
              backgroundColor: filterOn ? "#12903C" : alpha("#12903C", 0.1),
              borderColor: "#12903C",
              color: filterOn ? "#fff" : "#12903C",
              boxShadow: filterOn
                ? isDark
                  ? "0 6px 16px rgba(18, 144, 60, 0.5)"
                  : "0 6px 16px rgba(18, 144, 60, 0.4)"
                : isDark
                  ? "0 4px 12px rgba(18, 144, 60, 0.2)"
                  : "0 4px 12px rgba(18, 144, 60, 0.15)",
            },
          }}
        >
          {filterLabel} {filterOn ? "ON" : ""}
        </Button>
      </Box>
      <Stack
        spacing={1}
        direction="row"
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={onShowMap}
          startIcon={<LocationOnIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {showMapLabel}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onShowFav}
          startIcon={<FavoriteIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {favoritesLabel}
        </Button>
      </Stack>
      <ModalTalentFilter
        open={showFilter}
        onClose={() => setShowFilter(false)}
      />
    </GridToolbarContainer>
  );
};

const TalentsPage = () => {
  const independentProfessionalsLabel = useUILabel("talent.independentProfessionals");
  const ratingLabel = useUILabel("talent.rating");
  const workStatusLabel = useUILabel("talent.workStatus");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const soleProprietorLabel = useUILabel("talent.soleProprietor");
  const entrepreneurLabel = useUILabel("talent.entrepreneur");
  const partTimeLabel = useUILabel("talent.partTime");
  const talentLabel = useUILabel("talent.talent");

  useTitle(independentProfessionalsLabel);
  const navigate = useNavigate();
  const company = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Talent | null>(null);
  const searchFilter = useAppSelector(
    (state: RootState) => state.ui.talentFilter
  );

  const { data, isLoading, isFetching } = useGetTalentsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    load: "country,photo,skills,languages,talent,business_verticals,certifications",
    search: searchQuery,
    ...searchFilter,
  });
  const rowCount = React.useMemo(() => data?.meta?.total || 0, [data]);

  const rows: Talent[] | undefined = React.useMemo(
    () => filterPrivateTalent(data?.data, company),
    [data, company]
  );

  const columns: GridColDef<Talent>[] = React.useMemo(() => {
    const data: GridColDef<Talent>[] = [
      {
        field: "avatar",
        headerName: "",
        width: isMobile ? 40 : 50,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row) return null;
          return (
            <Avatar
              alt={`${params.row.first_name || talentLabel}`}
              src={`${params.row?.photo?.[0]?.original_url}`}
            />
          );
        },
      },
      {
        field: "first_name",
        headerName: independentProfessionalsLabel,
        flex: 1,
        minWidth: isMobile ? 140 : 150,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row) return null;
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setCurrentItem(params.row);
                setOpen(true);
              }}
            >
              {params.row.full_name || "N/A"}
            </Link>
          );
        },
      },
      {
        field: "rating",
        headerName: ratingLabel,
        headerAlign: "center",
        minWidth: isMobile ? 110 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row) return null;
          return (
            <Rating
              value={params.row.total_rating || 0}
              readOnly
              precision={0.5}
            />
          );
        },
      },
      // {
      //   field: "min_pay",
      //   headerName: "Rate ($)",
      //   minWidth: 150,
      //   sortable: true,
      //   filterable: true,
      //   valueGetter: (params: GridValueGetterParams<Talent>) =>
      //     `${params.row.min_pay || "?"} - ${params.row.max_pay || "?"}`,
      // },
      // {
      //   field: "email",
      //   headerName: "Email",
      //   headerAlign: "center",
      //   minWidth: 200,
      //   sortable: true,
      //   filterable: true,
      //   align: "center",
      // },
      {
        field: "work_status",
        headerName: workStatusLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<Talent>) => {
          if (!params || !params.row || !params.row.talent?.work_status) return "?";
          const status = params.row.talent.work_status;
          if (status === UserWorkStatus.SOLE_PROPRIETOR || status === "sole-proprietor") {
            return soleProprietorLabel;
          }
          if (status === UserWorkStatus.ENTREPRENEUR || status === "entrepreneur") {
            return entrepreneurLabel;
          }
          if (status === UserWorkStatus.PART_TIME || status === "part-time") {
            return partTimeLabel;
          }
          return "?";
        },
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row || !params.row.talent?.work_status) {
            return <Typography variant="body2">-</Typography>;
          }
          const status = params.row.talent.work_status;
          let displayText = "?";

          if (status === UserWorkStatus.SOLE_PROPRIETOR || status === "sole-proprietor") {
            displayText = soleProprietorLabel;
          } else if (status === UserWorkStatus.ENTREPRENEUR || status === "entrepreneur") {
            displayText = entrepreneurLabel;
          } else if (status === UserWorkStatus.PART_TIME || status === "part-time") {
            displayText = partTimeLabel;
          }

          return <Typography variant="body2">{displayText}</Typography>;
        },
      },
      {
        field: "city",
        headerName: cityLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<Talent>) => {
          if (!params || !params.row) return "";
          return params.row.city ? `${params.row.city.toUpperCase()}` : "";
        },
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row) return null;
          return (
            <Typography variant="body2">
              {params.row.city ? params.row.city.toUpperCase() : "-"}
            </Typography>
          );
        },
      },
      {
        field: "state",
        headerName: stateLabel,
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<Talent>) => {
          if (!params || !params.row) return "";
          return params.row.state ? `${params.row.state.toUpperCase()}` : "";
        },
        renderCell: (params: GridRenderCellParams<Talent>) => {
          if (!params || !params.row) return null;
          return (
            <Typography variant="body2">
              {params.row.state ? params.row.state.toUpperCase() : "-"}
            </Typography>
          );
        },
      },
    ];
    return data;
  }, [isMobile]);

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleShowMap = React.useCallback(() => {
    navigate(`/independent-professionals/map`);
  }, []);

  const handleShowFav = React.useCallback(() => {
    navigate(`/independent-professionals/favorites`);
  }, []);

  const CustomToolbar = React.useMemo(
    () => () =>
    (
      <TableToolbar
        onSearch={handleSearch}
        onShowMap={handleShowMap}
        onShowFav={handleShowFav}
      />
    ),
    [handleSearch, handleShowMap, handleShowFav]
  );

  return (
    <Box
      sx={{
        width: "auto",
        px: { xs: 0, sm: 3 },
        py: 3,
      }}
    >
      <CardBase
        title={independentProfessionalsLabel}
        useGlassCard={true}
        nested={false}
      >
        <GlassDataGrid
          height="85vh"
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount || 0}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[100]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={React.useCallback((newSortModel: GridSortModel) => {
            setSortModel(newSortModel);
          }, [])}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          rowHeight={72}
          density="compact"
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: "16px 20px !important",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02) !important",
              borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"} !important`,
              display: "flex !important",
              alignItems: "center",
              minHeight: "64px !important",
              width: "100% !important",
              visibility: "visible !important",
              opacity: "1 !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.875rem",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
          }}
        />
      </CardBase>

      <TalentDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </Box>
  );
};
export default TalentsPage;
